/**
 * SRES · Implicit Orebody Wireframe — Phase 2 · Feb 2026.
 *
 * Sibling of GradeShellMesh but coloured navy-teal to distinguish
 * "implicit wireframe from drillhole intercepts" from the gold
 * grade-shell that comes from the estimated block model.
 */
import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import * as THREE from "three";

import { api } from "@/lib/api";
import { worldToScene } from "@/lib/sceneCoords";

const NAVY_TEAL = "#2E86AB";

export default function ImplicitWireframeMesh({
  pid,
  cutoff,
  origin,
  verticalExaggeration = 1,
  visible = true,
  opacity = 0.5,
  wireframe = false,
}) {
  const q = useQuery({
    queryKey: ["cockpit-implicit-wireframe", pid, Number(cutoff).toFixed(3)],
    queryFn: () =>
      api
        .get(`/projects/${pid}/wireframes/implicit`, {
          params: { cutoff: Number(cutoff) },
        })
        .then((r) => r.data),
    enabled: !!pid && visible && Number.isFinite(cutoff),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const geometry = useMemo(() => {
    const data = q.data;
    if (!data || !data.vertices?.length || !data.indices?.length) return null;
    if (!origin) return null;

    const geom = new THREE.BufferGeometry();
    const n = data.vertices.length;
    const flat = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const [e, no, z] = data.vertices[i];
      const p = worldToScene(e, no, z, origin, verticalExaggeration);
      flat[i * 3] = p.x;
      flat[i * 3 + 1] = p.y;
      flat[i * 3 + 2] = p.z;
    }
    geom.setAttribute("position", new THREE.BufferAttribute(flat, 3));

    const idx =
      data.indices.length > 65535
        ? new Uint32Array(data.indices)
        : new Uint16Array(data.indices);
    geom.setIndex(new THREE.BufferAttribute(idx, 1));
    geom.computeVertexNormals();
    geom.computeBoundingSphere();
    return geom;
  }, [q.data, origin, verticalExaggeration]);

  const meshRef = useRef();

  useEffect(() => {
    return () => {
      if (geometry) geometry.dispose();
    };
  }, [geometry]);

  if (!visible || !geometry || !origin) return null;

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      renderOrder={3}
      data-testid="cockpit-implicit-wireframe"
    >
      <meshPhysicalMaterial
        color={NAVY_TEAL}
        emissive={NAVY_TEAL}
        emissiveIntensity={0.05}
        metalness={0.25}
        roughness={0.6}
        transparent
        opacity={opacity}
        depthWrite={false}
        wireframe={wireframe}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
